import {httpGatewayService} from "@/modules/common/services";
import storageService from "@/modules/common/services/storageService";
import {isPersistentCacheEnabled} from "@/modules/common/utils/persistentCache";

const baseRoute = 'business/subscriptionsmng/v1'
const baseRouteV3 = 'v3/license'

const OFFERINGS_TTL_MS = 60 * 60 * 1000; // 1h

const offeringsKeyPrefix = (businessUid) => `APP_MARKET_OFFERINGS_${businessUid}_`;
const offeringsCacheKey = (businessUid, type) => `${offeringsKeyPrefix(businessUid)}${type}`;

const createSubscription = async (params) => {
  let result;

  try {
    result = await httpGatewayService.post(`${baseRouteV3}/subscriptions`, params);
    result = result.data;
  } catch (error) {
    result = {code: 'error'};
    console.error(error);
  }

  return result;
}

const getActiveSubscriptions = async () => {
  try {
    const result = await httpGatewayService.get(`${baseRoute}/subscriptions?filter[purchase_state][in]=purchased,suspended,canceled`)
    return result.data.subscriptions.filter(subscription => subscription['is_active'])
  } catch (error) {
    console.error(error)
    return [];
  }
}

const clearOfferingsCache = (businessUid) => {
  if (businessUid) storageService.removeByPrefix(offeringsKeyPrefix(businessUid));
}

const getPaidSubscriptions = async () => {
  let result;

  try {
    result = await httpGatewayService.get(`${baseRoute}/subscriptions?filter[purchase_state]=purchased`)
    result = result.data.subscriptions;
  } catch (error) {
    result = {code: 'error'};
    console.error(error)
  }

  return result;
}

const getBillingAccount = async () => {
  let result;

  try {
    result = await httpGatewayService.get(`${baseRoute}/billing/accounts`)
    result = result.data.account;
  } catch (error) {
    result = {code: 'error'};
    console.error(error)
  }

  return result;
}

const getOfferings = async (type, {businessUid, force = false} = {}) => {
  const cacheKey = (isPersistentCacheEnabled() && businessUid) ? offeringsCacheKey(businessUid, type) : null;

  if (!force && cacheKey) {
    const cached = storageService.getWithExpiry(cacheKey);
    if (cached) return cached;
  }

  try {
    const queryParams = new URLSearchParams({
      is_active: 'true',
      page: '1',
      per_page: '100',
      sort: 'created_at:asc',
    });

    if (type) {
      queryParams.append('type', type);
    }

    const result = await httpGatewayService.get(`${baseRouteV3}/offerings?${queryParams.toString()}`);
    const offerings = result?.data?.offerings || [];

    if (cacheKey) {
      storageService.saveWithExpiry(cacheKey, offerings, OFFERINGS_TTL_MS);
    }

    return offerings;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const hasActiveSubscriptionToApp = async (appCodeName) => {
  let result;
  try {
    result = await httpGatewayService.get(`${baseRouteV3}/subscriptions?is_active=true&sku=${appCodeName}`, null, {skipTeamsViewFilter: true});
    // Return true if there are any active subscriptions for this app
    return result?.data?.subscriptions && result?.data?.subscriptions.length > 0;
  } catch (error) {
    console.error(error);
    return false;
  }
}

const cancelV3Subscription = async (subscriptionUid) => {
  try {
    const res = await httpGatewayService.put(`${baseRouteV3}/subscriptions/${subscriptionUid}`, {
      purchase_state: 'canceled',
    });
    return res.data;
  } catch (error) {
    console.error('Error canceling subscription:', subscriptionUid, error);
    throw error;
  }
}

const getOffering = async (offeringUid) => {
  try {
    const result = await httpGatewayService.get(`${baseRouteV3}/offerings/${offeringUid}`);
    return result?.data || null;
  } catch (error) {
    console.error('Error fetching offering:', offeringUid, error);
    return null;
  }
}

const getBundledOfferings = async (offeringUid) => {
  try {
    const queryParams = new URLSearchParams({ offering_uid: offeringUid });
    const result = await httpGatewayService.get(`${baseRouteV3}/bundled_offerings?${queryParams.toString()}`);
    return result?.data?.bundled_offerings || [];
  } catch (error) {
    console.error('Error fetching bundled offerings for offering:', offeringUid, error);
    return [];
  }
}

export const subscriptionManagerService = {
  createSubscription,
  getActiveSubscriptions,
  clearOfferingsCache,
  getPaidSubscriptions,
  getBillingAccount,
  getOfferings,
  getOffering,
  getBundledOfferings,
  hasActiveSubscriptionToApp,
  cancelV3Subscription,
};
